import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { ClipboardList, CalendarClock } from 'lucide-react';
import nurseryService from '../../services/nursery';
import LotPlaceFilter from '../../components/nursery/LotPlaceFilter';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import type { NurseryLot, LotStage, ProductionBoardFilters } from '../../types/nursery';

const STAGES: LotStage[] = ['sown', 'seedling', 'transplanted', 'grafted', 'budded', 'growing', 'potted', 'ready', 'dispatched'];

function ProductionBoard() {
  const orgTz = useOrgTimezone();
  const [lots, setLots] = useState<NurseryLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<ProductionBoardFilters>({});

  useEffect(() => {
    setLoading(true);
    setError(null);
    nurseryService.getProductionBoard(filters)
      .then(res => {
        if (res.success) setLots(res.data || []);
        else setError('Failed to load production board');
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [filters]);

  // Emergent collation into buckets: bareroot seasonal-window lots group together;
  // everything else groups by ready-date month.
  const buckets = useMemo(() => {
    const map = new Map<string, { label: string; sortKey: string; lots: NurseryLot[]; trees: number }>();
    for (const lot of lots) {
      let key: string;
      let label: string;
      let sortKey: string;
      if (lot.stock_type === 'B' && lot.readiness_mode === 'seasonal_window') {
        const yr = lot.ready_date ? lot.ready_date.slice(0, 4) : '----';
        key = `bareroot-${yr}`;
        label = `Bareroot dig season ${yr !== '----' ? yr : '(unscheduled)'}`;
        sortKey = `0-${yr}`;
      } else if (lot.ready_date) {
        const ym = lot.ready_date.slice(0, 7);
        key = `ready-${ym}`;
        label = `Ready ${ym}`;
        sortKey = `1-${ym}`;
      } else {
        key = 'unscheduled';
        label = 'No ready date yet';
        sortKey = '2';
      }
      if (!map.has(key)) map.set(key, { label, sortKey, lots: [], trees: 0 });
      const b = map.get(key)!;
      b.lots.push(lot);
      b.trees += lot.current_count;
    }
    return Array.from(map.values()).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [lots]);

  const set = (patch: Partial<ProductionBoardFilters>) => setFilters(f => ({ ...f, ...patch }));

  const totalTrees = lots.reduce((s, l) => s + l.current_count, 0);

  return (
    <>
      <PageHeader
        title="Production — What's Ready"
        subtitle={`${lots.length} lots · ${totalTrees.toLocaleString()} trees`}
        icon={<ClipboardList className="h-6 w-6 text-white" />}
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <select
            value={filters.stock_type ?? ''}
            onChange={e => set({ stock_type: e.target.value ? (e.target.value as 'B' | 'P') : undefined })}
            className="border border-neutral-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All types</option>
            <option value="B">Bareroot</option>
            <option value="P">Container</option>
          </select>

          <select
            value={filters.stage ?? ''}
            onChange={e => set({ stage: e.target.value ? (e.target.value as LotStage) : undefined })}
            className="border border-neutral-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All stages</option>
            {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <LotPlaceFilter
            value={{ farm_id: filters.farm_id, block_id: filters.block_id, row_number: filters.row_number, row_side: filters.row_side }}
            onChange={place => setFilters(f => ({
              ...f, farm_id: place.farm_id, block_id: place.block_id, row_number: place.row_number, row_side: place.row_side,
            }))}
          />

          <label className="text-sm text-neutral-500 flex items-center gap-2">
            Ready by
            <input
              type="date"
              value={filters.ready_to ?? ''}
              onChange={e => set({ ready_to: e.target.value || undefined })}
              className="border border-neutral-300 rounded-lg px-3 py-2 text-sm"
            />
          </label>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>
        )}

        {loading ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-neutral-100 rounded-lg animate-pulse" />)}</div>
        ) : buckets.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <CalendarClock className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">Nothing in production matches these filters</p>
            <p className="text-sm mt-1">Place lots and they'll collate here by readiness.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {buckets.map(bucket => (
              <div key={bucket.label} className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-200">
                  <h3 className="text-sm font-semibold text-neutral-800">{bucket.label}</h3>
                  <span className="text-xs text-neutral-500">{bucket.lots.length} lots · {bucket.trees.toLocaleString()} trees</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-neutral-400 text-left text-xs">
                    <tr>
                      <th className="px-4 py-2 font-medium">Cultivar</th>
                      <th className="px-4 py-2 font-medium">Type</th>
                      <th className="px-4 py-2 font-medium">Stage</th>
                      <th className="px-4 py-2 font-medium">Location</th>
                      <th className="px-4 py-2 font-medium text-right">Count</th>
                      <th className="px-4 py-2 font-medium">Ready</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {bucket.lots.map(lot => (
                      <tr key={lot.id} className="hover:bg-neutral-50">
                        <td className="px-4 py-2">
                          <Link to={`/nursery/lots/${lot.id}`} className="text-primary-600 hover:underline">
                            {lot.cultivar_name}{lot.rootstock_name ? ` / ${lot.rootstock_name}` : ''}
                          </Link>
                        </td>
                        <td className="px-4 py-2 text-neutral-600">{lot.tree_type_code}</td>
                        <td className="px-4 py-2 text-neutral-600">{lot.current_stage}</td>
                        <td className="px-4 py-2 text-neutral-600">
                          {lot.location_name || '—'}
                          {lot.block_name ? ` · ${lot.block_name}` : ''}
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-neutral-800">{lot.current_count.toLocaleString()}</td>
                        <td className="px-4 py-2 text-neutral-600">{lot.ready_date ? formatLocalDateInTz(lot.ready_date, orgTz) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default ProductionBoard;
